import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { indexStore } from "src/store/indexStore";
import Visibility from "src/Components/utils/Visibility";
import Iterator from "src/Components/utils/Iterator";
import { CustomerCardLoading } from "src/Components/Cards/CustomerCard";
import CustomerCard from "src/Components/Cards/CustomerCard";
import PaginationComponent from "src/Components/Pagination/PaginationComponent";
import { Input } from "src/Components/ui/input";
import { Button } from "src/Components/ui/button";
import { Card, CardContent } from "src/Components/ui/card";
import { Search, X, Users, Sparkles, Phone } from "lucide-react";

function Customers() {
  const {
    customersLoading,
    customers,
    customersPagination,
    setCustomersPagination,
    searchCustomerFunction,
  } = indexStore();

  const fetchCustomers = async (
    page = customersPagination.current_page,
    word = ""
  ) => {
    const data = {
      page,
      per_page: customersPagination.per_page,
      word: word,
    };
    await searchCustomerFunction(data);
  };
  useEffect(() => {
    fetchCustomers(1);
    
  }, []);
  const [searchWord, setSearchWord] = useState("");
  const handlePageChange = (page) => {
    setCustomersPagination({ ...customersPagination, current_page: page });
    fetchCustomers(page);
  };

  const { t } = useTranslation();
  const clearSearch = () => {
    setSearchWord("");
    fetchCustomers(1);
  };

  const hasSearch = searchWord !== "";

  return (
    <div className="flex flex-col h-full gap-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              {t("dashbord.customers")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("gloabl.manage_customers") ||
                "View and manage your customers"}
            </p>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("gloabl.phone") || "Search by phone number..."}
                onChange={(e) => setSearchWord(e.target.value)}
                value={searchWord}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchCustomers(1, searchWord);
                  }
                }}
                className="pl-9"
              />
            </div>
            <Button
              onClick={() => fetchCustomers(1, searchWord)}
              className="gap-2"
              disabled={customersLoading}
            >
              <Search className="w-4 h-4" />
              {t("gloabl.search")}
            </Button>
            <Button
              variant="outline"
              onClick={clearSearch}
              disabled={!hasSearch || customersLoading}
              className="gap-2"
            >
              <X className="w-4 h-4" />
              {t("gloabl.clear")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Customers Grid */}
      <div className="flex-1">
        <Visibility visible={!customersLoading && customers.length > 0}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">
                  {t("gloabl.customers_found") || "Customers"} (
                  {customersPagination.total || customers.length})
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <Iterator
                data={customers}
                render={(e) => <CustomerCard element={e} key={e.id} />}
              />
            </div>
          </div>
        </Visibility>

        <Visibility visible={customersLoading}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Iterator
              data={[1, 2, 3, 4, 5, 6]}
              render={(e, i) => <CustomerCardLoading key={i} />}
            />
          </div>
        </Visibility>

        <Visibility visible={!customersLoading && customers.length === 0}>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {hasSearch
                  ? t("gloabl.no_customers_found") || "No customers found"
                  : t("gloabl.no_customers") || "No customers yet"}
              </h3>
              <p className="text-sm text-muted-foreground text-center">
                {hasSearch
                  ? t("gloabl.try_different_search") ||
                    "Try a different search term"
                  : t("gloabl.customers_will_appear") ||
                    "Customers will appear here once they register"}
              </p>
            </CardContent>
          </Card>
        </Visibility>

        {/* Pagination */}
        <Visibility visible={!customersLoading && customers.length > 0}>
          <div className="mt-6">
            <PaginationComponent
              pagination={customersPagination}
              onPageChange={handlePageChange}
            />
          </div>
        </Visibility>
      </div>
    </div>
  );
}

export default Customers;
